import { type RefObject, useEffect, useState } from 'react';
import { useIntersectionObserver } from 'useIntersectionObserver';

const INITIAL_LIMIT = 20;
const INCREMENT = 10;
const OBSERVER_OPTIONS: IntersectionObserverInit = { rootMargin: '0px 0px 600px 0px' };

/*
    Shows 20 courses, then 10 more each time the sentinel below the list comes within 600px of the
    viewport. The observer reports only changes, so while the sentinel stays in range the limit keeps
    growing, one step per render. `resetKey` resets the limit to 20. The filter controls are at the top
    of the page, so the user does not see the list shrink.
*/
export function useLazyLoadLimit({
    sentinelRef,
    total,
    resetKey,
}: {
    sentinelRef: RefObject<Element | null>;
    total: number;
    resetKey: string;
}) {
    const [limit, setLimit] = useState(INITIAL_LIMIT);
    const [lastResetKey, setLastResetKey] = useState(resetKey);
    const isNearBottom = useIntersectionObserver(sentinelRef, OBSERVER_OPTIONS);

    if (resetKey !== lastResetKey) {
        setLastResetKey(resetKey);
        setLimit(INITIAL_LIMIT);
    }

    useEffect(() => {
        if (isNearBottom && limit < total) setLimit(current => current + INCREMENT);
    }, [isNearBottom, limit, total]);

    return limit;
}
